import React from "react";
import _ from "lodash";
import {parseResponse} from "../../../utils/api";
import {generateQueryParamsHash} from "../utils";
import {useTabsSharedDataContext} from "../components_v2/TabsSharedData/TabsSharedData";
import {isCancel, useCancelableRequest} from "../../../hooks/useCancelableRequest";

const RISK_RETURN_DATA_KEY = 'risk-return-data';

/**
 * Fetch portfolio risk return and correlation matrix data.
 * @param customerId Customer identifier
 * @param portfolio Portfolio data
 * @param dataProvider Data provider
 * @param useContext Flag, that indicate if data from context should be used or not.
 * Set to true in case hook wrapped in context provider that already fetch data and it could be reused.
 * @param startDate
 * @param endDate
 */
export function useRiskReturnData(customerId, portfolio, dataProvider, startDate, endDate, investmentStrategyId) {

  const [data, setData] = React.useState({
    riskReturnData: undefined,
    riskReturnLoading: true,
    riskReturnError: null,
    updatedAt: +new Date(),
  });

  const paramsForHash = [startDate, endDate, investmentStrategyId];
  const sharedData = useTabsSharedDataContext();
  const getCancelToken = useCancelableRequest();
  const dataKey = `${RISK_RETURN_DATA_KEY}-${portfolio.depotNumber}`;

  const fetch = () => {
    const cancelToken = getCancelToken();
    const _setData = (state, onlyCache=false) => {
      // It is not possible to sync data with shared data in
      // hook because hook is not executed if component destroyed
      if(!onlyCache) setData(state);
      sharedData.setDataValue(dataKey, hashKey, state);
    };

    const hashKey = generateQueryParamsHash(customerId, [portfolio], ...paramsForHash);

    _setData({
      riskReturnData: undefined,
      riskReturnLoading: true,
      riskReturnError: null,
      updatedAt: +new Date(),
    });
    dataProvider.getRiskReturn(customerId, portfolio.depotNumberForAssets || portfolio.depotNumber, startDate, endDate, undefined, cancelToken)
      .then((response) => {
        parseResponse(response, 'risk_return',
          (data) => {
            _setData({
              riskReturnError: undefined,
              riskReturnData: data,
              riskReturnLoading: false,
              updatedAt: +new Date()
            })
          },
          (error) => {
            _setData({
              riskReturnData: undefined,
              riskReturnLoading: false,
              riskReturnError: error,
              updatedAt: +new Date()
            })
          })
      })
      .catch((error) => {
        _setData({
          riskReturnData: undefined,
          riskReturnLoading: false,
          riskReturnError: error.toString(),
          updatedAt: +new Date()
        }, isCancel(error));
      })
  };

  const syncFromShared = () => {
    const sharedRiskReturnData = !!sharedData
        ? sharedData.getDataValue(dataKey, generateQueryParamsHash(customerId, [portfolio], ...paramsForHash))
        : undefined;

      if (!!sharedRiskReturnData && !sharedRiskReturnData.riskReturnError && sharedRiskReturnData.updatedAt !== data.updatedAt) {
        // sync internal hook data with shared data.
        setData(sharedRiskReturnData);

        return true;
      } else {
        return false
      }
  };

  React.useEffect(() => {syncFromShared()}, [_.get(sharedData, 'updatedAt')]);

  React.useEffect(() => {
    if (!!customerId && !!portfolio) {
      const synced = syncFromShared();

      if (!synced) {
        fetch();
      }
    }
  }, [customerId, _.get(portfolio, 'depotNumberForAssets'), _.get(portfolio, 'depotNumber'), startDate, endDate]);

  return data

}